import React from 'react';
import { Download, Trash2, Copy, Clock, ExternalLink, RotateCcw } from 'lucide-react';
import { QRCodeData } from '../types/qr-types';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../i18n/translations';

/**
 * 扩展 QRCodeData，添加历史记录需要的字段
 */
type QRHistoryItem = QRCodeData & {
  dataUrl: string;
  timestamp: number;
  content: string;
};

interface QRHistoryProps {
  history: QRHistoryItem[];
  onClearHistory: () => void;
  onRegenerate: (item: QRHistoryItem) => void;
}

const QRHistory: React.FC<QRHistoryProps> = ({ history, onClearHistory, onRegenerate }) => {
  const { language } = useLanguage();

  const downloadQRFromHistory = (qr: QRHistoryItem) => {
    if (!qr.dataUrl) return;

    const link = document.createElement('a');
    link.download = `qrcode-${qr.timestamp}.png`;
    link.href = qr.dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyContent = async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
    } catch (error) {
      console.error('Failed to copy content:', error);
    }
  };

  const formatTimestamp = (timestamp?: number) => {
    if (!timestamp) return t('historyPage.unknownTime', language);
    return new Date(timestamp).toLocaleDateString() + ' ' + new Date(timestamp).toLocaleTimeString();
  };

  const getTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      url: t('historyPage.types.url', language),
      text: t('historyPage.types.text', language),
      email: t('historyPage.types.email', language),
      phone: t('historyPage.types.phone', language),
      sms: t('historyPage.types.sms', language),
      wifi: t('historyPage.types.wifi', language)
    };
    return labels[type] || type;
  };

  const truncateContent = (content: string, maxLength: number = 50) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  // 类型保护函数，确保 type 字段存在
  function getTypeSafe(qr: QRHistoryItem): string {
    return (qr as QRCodeData).type;
  }

  if (history.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 max-w-md mx-auto">
          <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">{t('historyPage.empty.title', language)}</h3>
          <p className="text-gray-400">
            {t('historyPage.empty.description', language)}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">{t('historyPage.title', language)}</h2>
        <button
          onClick={onClearHistory}
          className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          {t('historyPage.clearAll', language)}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {history.map((qr, index) => (
          <div
            key={index}
            className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/15 transition-all duration-200"
          >
            {/* QR Code Preview */}
            <div className="text-center mb-4">
              {qr.dataUrl && (
                <div className="inline-block p-2 bg-white rounded-lg">
                  <img
                    src={qr.dataUrl}
                    alt={t('historyPage.qrCodeAlt', language)}
                    className="w-24 h-24 object-contain"
                  />
                </div>
              )}
            </div>

            {/* QR Info */}
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-block px-2 py-1 bg-blue-500/20 text-blue-300 text-xs rounded-full">
                    {getTypeLabel(getTypeSafe(qr))}
                  </span>
                </div>
                <p className="text-white text-sm font-medium">
                  {truncateContent(qr.content)}
                </p>
              </div>

              <div className="flex items-center gap-2 text-xs text-gray-400">
                <Clock className="w-3 h-3" />
                {formatTimestamp(qr.timestamp)}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => onRegenerate(qr)}
                  className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-lg transition-colors"
                >
                  <RotateCcw className="w-3 h-3" />
                  {language === 'zh' ? '重新生成' : 'Regenerate'}
                </button>
                <button
                  onClick={() => downloadQRFromHistory(qr)}
                  className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg transition-colors"
                >
                  <Download className="w-3 h-3" />
                </button>
                <button
                  onClick={() => copyContent(qr.content)}
                  className="flex items-center justify-center gap-1 px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white text-xs rounded-lg transition-colors"
                >
                  <Copy className="w-3 h-3" />
                </button>
                {getTypeSafe(qr) === 'url' && (
                  <button
                    onClick={() => window.open(qr.content, '_blank')}
                    className="flex items-center justify-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-xs rounded-lg transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QRHistory;